// Empty-record + duplicate factories for Templates and Emails, plus
// store-mutation helpers for in-place updates. Per spec/build-plan.md
// P-024 row deliverables + Tech Arch §2.4 / §2.5 + spec/build-plan.md
// P-032 row ("Save / duplicate / delete Email actions; 'Duplicate from
// previous Email' workflow on Home / Email list").
//
// **Why a single module.** Six call sites at P-024 (home / template-list /
// email-list × create + duplicate). P-031 added one more pattern —
// `updateTemplateInStore` — module-scope inside the editor. P-032 needs
// the parallel `updateEmail` plus a `createEmailFromTemplate` factory
// for the picker modal. The `updateTemplate / updateEmail` pair is the
// 2nd-consumer extraction trigger flagged at D-093; both lift here so
// the screens read as one-line dispatches.
//
// **Empty Email's `templateId` / `templateName` are empty strings.**
// EmailSchema's fields are `z.string()` (no `.min(1)`), so the empty value
// is valid. P-032's new-email picker handles the "pick a template" UX up
// front; the empty-Email path remains as the "Start blank" affordance.
//
// **`createEmailFromTemplate(template)` snapshots the Template into an
// Email at creation time** per Tech Arch §2.5: copy `instanceId`,
// `blockId`, `options`; initialize `inputs` from each Block's
// `defaultInputs`; set `included: true`; capture `templateId` +
// `templateName` for provenance. After creation the Email is independent
// of the Template (editing the Template later does NOT affect this
// Email).
//
// **Duplicate appends `" (copy)"` to the name.** Standard idiom; users
// recognize the convention from desktop file managers + Google Docs. The
// duplicated record gets a fresh `id`, a fresh `createdAt` / `updatedAt`,
// and a fresh `instanceId` per Block (so the duplicate is independent of
// the source — editing one doesn't affect the other).
//
// **`updateTemplate(id, updater)` / `updateEmail(id, updater)` no-op
// behavior.** When the updater returns the SAME reference, the store's
// `setState` no-op detection (D-082) skips listener fan-out — but only if
// the wrapper itself returns the same AppState reference. The wrappers
// short-circuit (return `s` unchanged) when no record matched the id
// AND when the updater returned the same record reference. Mirrors the
// same-reference contract documented at template-editor-helpers.ts
// (D-093) so unknown-id / unknown-instanceId / out-of-bounds operations
// don't churn the listener queue.

import { blocks, type BlockId } from '../registry/blocks'
import { newId } from '../lib/id'
import type { Email, EmailBlockInstance, Template, TemplateBlockInstance } from './schemas'
import { CURRENT_SCHEMA_VERSION } from './schemas'
import { store } from './store'

function nowIso(): string {
  return new Date().toISOString()
}

export function createEmptyTemplate(name = 'Untitled template'): Template {
  const now = nowIso()
  return {
    id: newId(),
    name,
    createdAt: now,
    updatedAt: now,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    blocks: [],
  }
}

export function createEmptyEmail(name = 'Untitled email'): Email {
  const now = nowIso()
  return {
    id: newId(),
    name,
    templateId: '',
    templateName: '',
    createdAt: now,
    updatedAt: now,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    subjectLine: '',
    previewText: '',
    blocks: [],
  }
}

export function duplicateTemplate(source: Template): Template {
  const now = nowIso()
  const blocks: TemplateBlockInstance[] = source.blocks.map((b) => ({
    ...b,
    instanceId: newId(),
  }))
  return {
    ...source,
    id: newId(),
    name: `${source.name} (copy)`,
    createdAt: now,
    updatedAt: now,
    blocks,
  }
}

export function duplicateEmail(source: Email): Email {
  const now = nowIso()
  const blocks: EmailBlockInstance[] = source.blocks.map((b) => ({
    ...b,
    instanceId: newId(),
  }))
  return {
    ...source,
    id: newId(),
    name: `${source.name} (copy)`,
    createdAt: now,
    updatedAt: now,
    blocks,
  }
}

/**
 * Insert a new empty Template into the global store and navigate to its
 * editor route. Used by the "New Template" CTA on the Home + Template
 * list screens (and any future surface that needs the same handler shape).
 * Returns the created Template so callers can assert against it in tests.
 */
export function createAndOpenTemplate(): Template {
  const t = createEmptyTemplate()
  store.setState((s) => ({ ...s, templates: [...s.templates, t] }))
  window.location.hash = `#/templates/${t.id}`
  return t
}

/**
 * Insert a new empty Email into the global store and navigate to its
 * editor route. Mirror of `createAndOpenTemplate`. Used by the "Start
 * blank" affordance on the new-email picker modal (P-032).
 */
export function createAndOpenEmail(): Email {
  const e = createEmptyEmail()
  store.setState((s) => ({ ...s, emails: [...s.emails, e] }))
  window.location.hash = `#/emails/${e.id}`
  return e
}

/**
 * Build an Email from a Template per Tech Arch §2.5: snapshot
 * `instanceId / blockId / options` from the Template; initialize each
 * Block's `inputs` from the registered Block's `defaultInputs`; capture
 * `templateId / templateName` for provenance; default `included: true`.
 *
 * After this call the Email is independent of the source Template —
 * editing the Template later does NOT affect this Email; deleting the
 * Template does NOT break this Email (the templateName stays for
 * display).
 *
 * Unknown blockIds in the source Template (defensive — should not happen
 * under normal flow, but JSON import or a future registry rename could
 * drift) carry a fresh `instanceId` + the source `options`; `inputs` is
 * `undefined` (the editor will surface the "unknown block" placeholder
 * the same way the Template editor does at P-031). The orchestrator
 * (`prepareBlock`, P-018) throws on unknown blockId at render time, so
 * the Email stays editable but won't render until the bad Block is
 * removed.
 */
export function createEmailFromTemplate(template: Template, name?: string): Email {
  const now = nowIso()
  const blockInstances: EmailBlockInstance[] = template.blocks.map((b) => {
    const def = blocks[b.blockId as BlockId]
    return {
      instanceId: newId(),
      blockId: b.blockId,
      options: b.options,
      inputs: mergeDefaultInputs(def?.defaultInputs, b.defaultInputs),
      included: true,
    }
  })
  return {
    id: newId(),
    name: name ?? `New email — ${template.name}`,
    templateId: template.id,
    templateName: template.name,
    createdAt: now,
    updatedAt: now,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    subjectLine: '',
    previewText: '',
    blocks: blockInstances,
  }
}

/**
 * Merge order at Email creation per Tech Arch §2.5 (P-037.31 / D-139):
 * `BlockDefinition.defaultInputs` (registry-level base) → spread Template-
 * instance `defaultInputs` (per-instance override) → result becomes the
 * new Email's `inputs`. Email author then edits freely; subsequent Template
 * edits do NOT propagate to existing Emails (snapshot model).
 *
 * Missing per-instance defaults (the common case pre-v12 + the post-v12
 * case for Blocks the Template editor doesn't surface Defaults UI for)
 * fall through to the registry defaults — existing pre-D-139 behavior
 * preserved. An empty per-instance object (`{}`) is a no-op spread — does
 * NOT override anything; same outcome as the missing case.
 *
 * Empty-string overrides (e.g., a Template setting `headline: ''` on a
 * section-heading instance) WILL override the registry default — staff
 * gets the empty string in the new Email's `inputs`, not the registry
 * placeholder. This is intentional: the Template editor's Defaults form
 * surfaces the same FormGenerator the Email editor uses; staff who clear
 * a Template default expect the cleared value to flow through (not silently
 * fall back to the registry seed).
 *
 * Non-object `registry` shape (defensive — unknown blockId returns
 * `undefined` from the registry lookup) yields the per-instance map
 * unchanged. Non-object `perInstance` shape (defensive — should not
 * occur under normal flow, but `unknown` typing allows anything) yields
 * the registry default unchanged. Both arms preserve `undefined`
 * passthrough when neither side carries data.
 */
function mergeDefaultInputs(registry: unknown, perInstance: unknown): unknown {
  if (!isPlainObject(perInstance)) return registry
  if (!isPlainObject(registry)) return perInstance
  return { ...registry, ...perInstance }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Snapshot a Template into a fresh Email + insert + navigate. Used by
 * the new-email picker modal's "Pick a template" path (P-032).
 */
export function createAndOpenEmailFromTemplate(template: Template): Email {
  const e = createEmailFromTemplate(template)
  store.setState((s) => ({ ...s, emails: [...s.emails, e] }))
  window.location.hash = `#/emails/${e.id}`
  return e
}

/**
 * Duplicate an Email + insert into store + navigate to the new editor
 * route. Used by the new-email picker modal's "Duplicate from previous
 * Email" path AND the Email editor screen's Duplicate action (P-032).
 */
export function duplicateAndOpenEmail(source: Email): Email {
  const dup = duplicateEmail(source)
  store.setState((s) => ({ ...s, emails: [...s.emails, dup] }))
  window.location.hash = `#/emails/${dup.id}`
  return dup
}

/**
 * Duplicate a Template + insert into store + navigate to the new editor
 * route. Used by the Template editor screen's Duplicate action (P-031);
 * lifted here at P-032 to centralize the create-or-duplicate-and-navigate
 * pattern alongside its Email-side parallel.
 */
export function duplicateAndOpenTemplate(source: Template): Template {
  const dup = duplicateTemplate(source)
  store.setState((s) => ({ ...s, templates: [...s.templates, dup] }))
  window.location.hash = `#/templates/${dup.id}`
  return dup
}

/**
 * Apply `updater` to the Template with the given `id`. The updater
 * receives the current Template and returns its successor; if it returns
 * the SAME reference, the wrapper short-circuits (returns the same
 * AppState reference, which the store's no-op detection then skips).
 * No-op also when no Template matches `id`.
 *
 * Lifted here at P-032 from `template-editor.tsx`'s `updateTemplateInStore`
 * (P-031) at the 2nd-consumer trigger — Email editor's parallel
 * `updateEmail` brings the second screen-level update flow.
 */
export function updateTemplate(id: string, updater: (t: Template) => Template): void {
  store.setState((s) => {
    const idx = s.templates.findIndex((t) => t.id === id)
    if (idx === -1) return s
    const current = s.templates[idx]!
    const next = updater(current)
    if (next === current) return s
    const templates = s.templates.slice()
    templates[idx] = next
    return { ...s, templates }
  })
}

/**
 * Apply `updater` to the Email with the given `id`. Mirror of
 * `updateTemplate`; same no-op semantics.
 */
export function updateEmail(id: string, updater: (e: Email) => Email): void {
  store.setState((s) => {
    const idx = s.emails.findIndex((e) => e.id === id)
    if (idx === -1) return s
    const current = s.emails[idx]!
    const next = updater(current)
    if (next === current) return s
    const emails = s.emails.slice()
    emails[idx] = next
    return { ...s, emails }
  })
}
